// ─── TCU computation (Phase E — CAP) ────────────────────────────────────────
// Pure domain logic: converts student dedication hours into the TCU band the
// TMAI standard allows, and checks a declared TCU against it. Consistency is
// evaluated against the RAW band (hours ÷ standard range, inclusive), never
// against the rounded display values — a declared integer like "3 TCU" for
// 130h UG (band 2.89–3.51, shown as 2.9–3.5) must pass.
const EPSILON = 1e-9;
const round1 = (n) => Math.round(n * 10) / 10;
/**
 * Computes the TCU band for a course's time allocation at the given level,
 * optionally validating a declared TCU against it.
 *
 * Standard: UG 37–45 h/TCU · grad 50–60 h/TCU (from `framework.tcuStandard`).
 */
export function computeTcu(timeAllocation, level, framework, declaredTcu) {
    const hours = [
        timeAllocation.lecture,
        timeAllocation.reading,
        timeAllocation.homework,
        timeAllocation.projects,
        timeAllocation.exams,
    ];
    for (const h of hours) {
        if (!Number.isFinite(h) || h < 0) {
            throw new Error(`TimeAllocation hours must be finite and non-negative: ${h}`);
        }
    }
    const totalHours = hours.reduce((s, h) => s + h, 0);
    const [lowerHours, upperHours] = level === 'undergraduate'
        ? framework.tcuStandard.undergraduateHoursPerTcu
        : framework.tcuStandard.graduateHoursPerTcu;
    const rawMin = totalHours / upperHours;
    const rawMax = totalHours / lowerHours;
    const result = {
        totalHours,
        minTcu: round1(rawMin),
        maxTcu: round1(rawMax),
        suggestedTcu: suggestTcu(rawMin, rawMax),
    };
    if (declaredTcu !== undefined) {
        if (!Number.isFinite(declaredTcu) || declaredTcu < 0) {
            throw new Error(`Declared TCU must be finite and non-negative: ${declaredTcu}`);
        }
        result.declaredTcu = declaredTcu;
        if (declaredTcu > 0) {
            result.declaredHoursPerTcu = round1(totalHours / declaredTcu);
        }
        result.isConsistent = declaredTcu >= rawMin - EPSILON && declaredTcu <= rawMax + EPSILON;
    }
    return result;
}
function suggestTcu(rawMin, rawMax) {
    const midpoint = (rawMin + rawMax) / 2;
    let best = null;
    // Math.max also normalizes the -0 that Math.ceil yields for a 0-hours band.
    for (let n = Math.max(0, Math.ceil(rawMin - EPSILON)); n <= Math.floor(rawMax + EPSILON); n++) {
        if (best === null || Math.abs(n - midpoint) < Math.abs(best - midpoint))
            best = n;
    }
    return best !== null ? best : round1(midpoint);
}
//# sourceMappingURL=cap-tcu.service.js.map